/**
 * Total-view Client expand: per-YOA openings + PL-GP/GPOI/PBT/PAT formulas.
 * Kept separate from plMultiTotalView to avoid pulling WP/draft deps into this path.
 */

import {
    plComputeComputedAmounts,
    plIsProtectedParentHeader,
    recomputePlDerivedFormulaNodes,
} from '@/pages/components/fs-report/FsFormula';
import {FS_CODE, findNodeByCodes} from '@/utils/fsCodes';
import {hasChildren} from '@/utils/fsDerived';
import {
    applyPlCotCosPurOverrides,
    resolvePlCotCosAmountForParentRollup,
    type PlCotInputMode,
} from '@/utils/plCostOfSalesVisibility';

export type PlClientYoaColumn = {yoa: string; sliceKey?: string};

export function listPlClientYoaColumns(
    periods: Array<{key?: string; yearOfAssessment?: string | null} | null | undefined> | null | undefined,
): PlClientYoaColumn[] {
    const out: PlClientYoaColumn[] = [];
    const seen = new Set<string>();
    for (const p of periods || []) {
        if (!p) continue;
        const yoa = String(p.yearOfAssessment || '').trim();
        if (!yoa || seen.has(yoa)) continue;
        seen.add(yoa);
        out.push({yoa, sliceKey: p.key});
    }
    return out;
}

/** Mirror ProfitLoss computedClientMap for one YOA leaf map. */
export function buildPlClientMapWithFormulas(
    fsTree: any[] | null | undefined,
    leafClientByFsId: Record<number, number>,
    _showCosRowInAdjustment: boolean,
    rawCosOpening = 0,
): Record<number, number> {
    const tree = (fsTree || []) as any[];
    const leafGetter = (node: any): number => {
        if (plIsProtectedParentHeader(node)) return 0;
        return Number(leafClientByFsId[Number(node.fsId)] ?? 0) || 0;
    };
    const computed = plComputeComputedAmounts(tree, leafGetter);

    const purNode = findNodeByCodes(tree, ['PL-COT-PUR']);
    if (purNode) {
        computed[Number((purNode as any).fsId)] =
            Number(leafClientByFsId[Number((purNode as any).fsId)] ?? 0) || 0;
    }

    const sumNode = (node: any): number => {
        if (!node) return 0;
        if (plIsProtectedParentHeader(node)) return 0;
        if (!hasChildren(node)) return Number(leafClientByFsId[Number(node.fsId)] ?? 0) || 0;
        let sum = 0;
        for (const ch of (node.children || [])) sum += sumNode(ch);
        return sum;
    };

    const COST_TURNOVER = findNodeByCodes(tree, FS_CODE.PL.COST_OF_TURNOVER);
    const COST_SALES = findNodeByCodes(tree, ['PL-COT-COS']);
    const OPEN_INV = findNodeByCodes(tree, ['PL-COT-OI']);
    const CLOSE_INV = findNodeByCodes(tree, ['PL-COT-CI']);
    const ROYALITY = findNodeByCodes(tree, ['PL-COT-ROY']);
    const SUBCONTRACT = findNodeByCodes(tree, ['PL-COT-SUB']);
    const OTHER_COT = findNodeByCodes(tree, ['PL-COT-OCT']);

    if (COST_TURNOVER) {
        let costSum = 0;
        if (COST_SALES || ROYALITY || SUBCONTRACT || OTHER_COT || purNode) {
            const purAmt = purNode ? sumNode(purNode) : 0;
            const primaryCost = resolvePlCotCosAmountForParentRollup({
                rawCos: rawCosOpening,
                openingInv: sumNode(OPEN_INV),
                purchases: purAmt,
                closingInv: sumNode(CLOSE_INV),
            });
            costSum = primaryCost
                + sumNode(ROYALITY) + sumNode(SUBCONTRACT) + sumNode(OTHER_COT);
        } else {
            costSum = sumNode(COST_TURNOVER);
        }
        computed[Number((COST_TURNOVER as any).fsId)] = costSum;
    }

    recomputePlDerivedFormulaNodes(tree, computed, leafGetter);
    return {...leafClientByFsId, ...computed};
}

function shallowCloneNode(vo: any): any {
    const children = Array.isArray(vo?.children) ? vo.children.map(shallowCloneNode) : [];
    return {...vo, children};
}

/**
 * Per-YOA Client map for Total view expand columns.
 * Leaf openings from each period; GP/GPOI/PBT/PAT via same formulas as Multiple Tab Client.
 * WHY: FS 落库会清公式侧；每列 YOA 须与 aggregate Client 一样走 applyPlCotCosPurOverrides。
 */
export function buildPerYoaPlClientByFsId(
    periods: Array<{key?: string; yearOfAssessment?: string | null; plLine?: any[]} | null | undefined> | null | undefined,
    convertForYoa?: (vo: any, yoa: string) => any,
    engagementPlLine?: any[] | null,
    options?: {
        formulaTree?: any[] | null;
        showCosRowInAdjustment?: boolean;
        cotInputMode?: PlCotInputMode;
        hideCostOfSalesRow?: boolean;
        bsLastYearAudited?: number;
        bsClientCurrent?: number;
    },
): Record<string, Record<number, number>> {
    const result: Record<string, Record<number, number>> = {};
    const fallback = Array.isArray(engagementPlLine) ? engagementPlLine : [];
    const formulaTree = options?.formulaTree;
    const cotInputMode = options?.cotInputMode ?? 'COS';
    const hideCostOfSalesRow = options?.hideCostOfSalesRow ?? false;
    const bsLastYearAudited = Number(options?.bsLastYearAudited ?? 0) || 0;
    const bsClientCurrent = Number(options?.bsClientCurrent ?? 0) || 0;
    for (const p of periods || []) {
        if (!p) continue;
        const yoa = String(p.yearOfAssessment || '').trim();
        if (!yoa) continue;
        const source = Array.isArray(p.plLine) && p.plLine.length > 0 ? p.plLine : fallback;
        const tree = convertForYoa && yoa
            ? source.map((vo) => convertForYoa(vo, yoa))
            : source.map(shallowCloneNode);
        const byFsId: Record<number, number> = {};
        const walk = (nodes: any[]) => {
            for (const n of nodes || []) {
                const children = Array.isArray(n?.children) ? n.children : [];
                if (children.length > 0) walk(children);
                const fsId = Number(n?.fsId);
                if (!Number.isFinite(fsId) || fsId <= 0) continue;
                byFsId[fsId] = Number(n?.openingAmount ?? n?.client ?? n?.amount ?? 0) || 0;
            }
        };
        walk(tree);
        const structure = (Array.isArray(formulaTree) && formulaTree.length > 0) ? formulaTree : tree;

        const oiNode = findNodeByCodes(structure, ['PL-COT-OI']);
        const purNode = findNodeByCodes(structure, ['PL-COT-PUR']);
        const ciNode = findNodeByCodes(structure, ['PL-COT-CI']);
        const cosNode = findNodeByCodes(structure, ['PL-COT-COS']);
        const oiId = Number((oiNode as any)?.fsId);
        const purId = Number((purNode as any)?.fsId);
        const ciId = Number((ciNode as any)?.fsId);
        const cosId = Number((cosNode as any)?.fsId);
        const rawCosOpening = Number.isFinite(cosId) ? (byFsId[cosId] ?? 0) : 0;
        const rawPurValue = Number.isFinite(purId) ? (byFsId[purId] ?? 0) : 0;
        const showCos = applyPlCotCosPurOverrides({
            out: byFsId,
            cotInputMode,
            hideCostOfSalesRow,
            bsLastYearAudited,
            bsClientCurrent,
            oiId: Number.isFinite(oiId) ? oiId : undefined,
            purId: Number.isFinite(purId) ? purId : undefined,
            ciId: Number.isFinite(ciId) ? ciId : undefined,
            cosId: Number.isFinite(cosId) ? cosId : undefined,
            rawCosOpening,
            rawPurValue,
        });

        result[yoa] = buildPlClientMapWithFormulas(
            structure,
            byFsId,
            options?.showCosRowInAdjustment ?? showCos,
            rawCosOpening,
        );
    }
    return result;
}
